import {
  CONTRACT_FIELDS,
  sameContractValue,
  type ContractField,
  type ContractSnapshot,
  type DiffRow,
} from './catalogueDiff.js';

/**
 * The web side of connector self-repair: turning the server's proposal into
 * rows a person can read, and the notes that stop the next round from
 * repeating this one.
 */

/** One round of the loop, kept so the next request can say what was already tried. */
export interface RepairAttempt {
  explanation: string;
  patch: Record<string, unknown>;
  /** Why the test after it still failed, or null when it passed. */
  error: string | null;
}

/** What the assistant will ask for at most, by kind. A video test is billed every time. */
export const MAX_AUTO_ATTEMPTS = 3;
export const MAX_AUTO_ATTEMPTS_VIDEO = 2;

export function clampAutoAttempts(requested: number, capability: string): number {
  const cap = capability === 'VIDEO' ? MAX_AUTO_ATTEMPTS_VIDEO : MAX_AUTO_ATTEMPTS;
  if (!Number.isFinite(requested)) return 1;
  return Math.min(cap, Math.max(1, Math.floor(requested)));
}

/** The changed fields of a proposal as before/after rows, in the form's order. */
export function patchRows(current: ContractSnapshot, patch: Record<string, unknown>): DiffRow[] {
  const rows: DiffRow[] = [];
  for (const field of CONTRACT_FIELDS) {
    if (!(field in patch)) continue;
    if (sameContractValue(current[field], patch[field])) continue;
    rows.push({ field, before: current[field], after: patch[field], checkedByDefault: true });
  }
  return rows;
}

/** The body of the update call: only contract fields, whatever else the proposal carried. */
export function patchToUpdateInput(patch: Record<string, unknown>): Partial<Record<ContractField, unknown>> {
  const input: Partial<Record<ContractField, unknown>> = {};
  for (const field of CONTRACT_FIELDS) {
    if (field in patch) input[field] = patch[field];
  }
  return input;
}

const HINT_LIMIT = 3_800;

const clip = (text: string, limit: number) => (text.length > limit ? `${text.slice(0, limit)}…` : text);

/** The person's note plus the earlier rounds, as one text for the repair request. */
export function attemptsHint(note: string, attempts: RepairAttempt[]): string {
  const parts: string[] = [];
  if (note.trim()) parts.push(`Note from the person: ${note.trim()}`);
  if (attempts.length > 0) {
    parts.push(
      'Earlier attempts in this session were applied and the test was run again. Do not repeat them; try something different:',
    );
    attempts.forEach((attempt, index) => {
      parts.push(
        `${index + 1}. Changed: ${clip(JSON.stringify(attempt.patch), 700)}` +
          (attempt.explanation ? ` (reason given: ${clip(attempt.explanation, 200)})` : '') +
          ` -> ${attempt.error === null ? 'the test passed' : `the test still failed: ${clip(attempt.error, 300)}`}`,
      );
    });
  }
  return clip(parts.join('\n'), HINT_LIMIT);
}
